/**
 * `TableViewSettings` — the per-view shape of spec M1-B4 §4.
 *
 * monday stores **nothing** here (`BoardView.settings_str` is `{}` for all 190 views in the
 * account `[vi§1]`), so there is no parity constraint: this is our shape, and §4 defines it.
 *
 * It deliberately does **not** reuse `packages/shared/src/views/table`'s snake_case
 * `tableViewSettingsSchema`: that one predates B4 and carries a different, smaller key set
 * (`hidden_columns` / `column_order` / `column_widths` / `frozen_columns` / `collapsed_groups`)
 * with no filter, sort, group-by or item height. Reconciling the two is a `packages/shared`
 * change and this round does not own that package — open question raised in `tasks/T-B4.md`.
 * The mapping is 1:1 for the keys that exist in both, so the merge is mechanical.
 *
 * Nothing in this file touches React: it is pure data + pure functions, unit-tested.
 */
import type { ColumnId, FilterOperator, GroupId } from '@elizmonday/shared';

export type SortDirection = 'asc' | 'desc';

export interface SortRule {
  readonly columnId: ColumnId;
  readonly direction: SortDirection;
}

export interface FilterRule {
  readonly columnId: ColumnId;
  readonly operator: FilterOperator;
  readonly compareValue: unknown;
  readonly compareAttribute?: string;
}

/** === monday's `ItemsQueryGroup`, which is why `create_view(filter:)` maps 1:1 `[sdl]`. */
export interface FilterGroup {
  readonly operator: 'and' | 'or';
  readonly rules: readonly FilterRule[];
  readonly groups: readonly FilterGroup[];
}

export interface GroupBySetting {
  readonly columnId: ColumnId;
  readonly direction: SortDirection;
  readonly showEmptyGroups: boolean;
}

export type ItemHeight = 'small' | 'medium' | 'large';

export interface TableViewSettings {
  /** Per-view override of `columns.position`; unknown ids are ignored on read. */
  readonly columnOrder?: readonly ColumnId[];
  readonly hiddenColumnIds: readonly ColumnId[];
  /** The sticky region. `['name']` by default; pinned ids must be a prefix (Q167). */
  readonly pinnedColumnIds: readonly ColumnId[];
  /**
   * `Wrap text`, monday's second column-menu row, **measured in 6i** (`[c:column-wrap-text]`):
   * it is a per-column flag whose effect is board-wide — with any column wrapped monday's grid
   * grows from a 36 px row pitch to **56 px** and every cell's `line-height` follows. So the
   * flag is per column (this list) and `WRAP_ROW_HEIGHT` is what the grid then measures at.
   */
  readonly wrappedColumnIds: readonly ColumnId[];
  readonly filter: FilterGroup | null;
  readonly sort: readonly SortRule[];
  readonly groupBy: GroupBySetting | null;
  /** `small` = the measured 36 px row. `medium`/`large` are accepted and ignored (Q173, E14). */
  readonly itemHeight: ItemHeight;
  /** Per-user, per-view; not part of "Save to this view" `[B2§8.3]`. */
  readonly collapsedGroupIds: readonly GroupId[];
  /** Board search. Lives in `view_user_state` and is **never** saved to the view `[B4§8.9]`. */
  readonly search: string;
  /** Reserved until Q49; `columns.width` is the store. */
  readonly columnWidths?: Readonly<Record<string, number>>;
  /** Reserved (Q173) — accepted, round-tripped, never evaluated. */
  readonly conditionalColoring?: unknown;
  readonly defaultItemValues?: Readonly<Record<string, unknown>>;
}

export const EMPTY_FILTER_GROUP: FilterGroup = { operator: 'and', rules: [], groups: [] };

export const DEFAULT_TABLE_VIEW_SETTINGS: TableViewSettings = {
  hiddenColumnIds: [],
  pinnedColumnIds: ['name' as ColumnId],
  wrappedColumnIds: [],
  filter: null,
  sort: [],
  groupBy: null,
  itemHeight: 'small',
  collapsedGroupIds: [],
  search: '',
};

/** The row height each `itemHeight` renders at. Only `small` is measured `[B4§8.1]`. */
export const ITEM_HEIGHT_PX: Readonly<Record<ItemHeight, number>> = {
  small: 36,
  medium: 44,
  large: 56,
};

/**
 * The row height a board with at least one wrapped column renders at — **56 px**, measured on
 * monday (`[c:column-wrap-text]`: the grid's row pitch goes 36 → 56 the moment `Wrap text` is
 * picked, and back again when it is picked a second time). It is deliberately the same number
 * as `ITEM_HEIGHT_PX.large`, which is where two lines of 14 px text fit.
 */
export const WRAP_ROW_HEIGHT = 56;

/** The row height the grid measures at: wrapping wins over `itemHeight`, as it does on monday. */
export function effectiveRowHeight(settings: TableViewSettings): number {
  return settings.wrappedColumnIds.length > 0
    ? Math.max(WRAP_ROW_HEIGHT, ITEM_HEIGHT_PX[settings.itemHeight])
    : ITEM_HEIGHT_PX[settings.itemHeight];
}

/* ── overlay ──────────────────────────────────────────────────────────────── */

/**
 * §5.1: `effectiveSettings` = the saved view settings with the caller's personal state on top.
 * Keys the personal state does not carry fall through, which is what makes "my filter" and
 * "the view's filter" coexist (E9).
 */
export function overlaySettings(
  saved: TableViewSettings,
  personal: Partial<TableViewSettings> | null | undefined,
): TableViewSettings {
  if (!personal) return saved;
  return { ...saved, ...stripUndefined(personal) };
}

function stripUndefined<T extends object>(input: T): Partial<T> {
  const out: Partial<T> = {};
  for (const [key, value] of Object.entries(input) as [keyof T, T[keyof T]][]) {
    if (value !== undefined) out[key] = value;
  }
  return out;
}

/**
 * `view.dirty` — what enables "Save to this view". Disabled in every capture where nothing was
 * changed `[ui:library/table-08-sort-pane.png]`, `[…table-13-group-by.png]`.
 *
 * `search` and `collapsedGroupIds` are personal-only and never make a view dirty `[B4§8.9]`.
 */
export function isDirty(saved: TableViewSettings, effective: TableViewSettings): boolean {
  return savedShape(saved) !== savedShape(effective);
}

function savedShape(settings: TableViewSettings): string {
  return JSON.stringify({
    columnOrder: settings.columnOrder ?? null,
    hiddenColumnIds: [...settings.hiddenColumnIds],
    pinnedColumnIds: [...settings.pinnedColumnIds],
    wrappedColumnIds: [...settings.wrappedColumnIds],
    filter: settings.filter,
    sort: settings.sort,
    groupBy: settings.groupBy,
    itemHeight: settings.itemHeight,
  });
}

/** What "Save to this view" persists: the shared half, never the personal half `[B4§8.5]`. */
export function sharedSettings(settings: TableViewSettings): TableViewSettings {
  return {
    ...settings,
    search: '',
    collapsedGroupIds: [],
  };
}

/* ── stale column references (P-268) ──────────────────────────────────────── */

/**
 * One saved entry the server **dropped** because the column it named is no longer active, as
 * `boards.read` reports it. The shape is `apps/api/src/services/filter-compiler.ts`'s
 * `IgnoredColumnRef`; it is re-declared here rather than imported because `packages/shared` is
 * not where either half lives and the web must not depend on `apps/api`.
 */
export interface IgnoredColumnRef {
  readonly kind:
    | 'sort'
    | 'filter'
    | 'groupBy'
    | 'hiddenColumns'
    | 'columnOrder'
    | 'frozenColumns'
    | 'pinnedColumns'
    | 'columnWidths'
    | 'wrappedColumns';
  readonly columnId: string;
}

/**
 * **P-268.** Removes every reference to the columns the server just told us it ignored.
 *
 * The board is already rendering when this runs — the read succeeded, the stale entries were
 * simply not applied — so this is not a repair, it is *forgetting*: without it the client
 * would send the same dead sort on every load, "Sort / 4" would keep counting rules that do
 * nothing, and the pane would offer a rule whose column has no name to show.
 *
 * monday drops such an entry silently, so there is no toast and no undo. Returns the same
 * object when nothing referenced a dropped column, which is what lets the caller skip the
 * write-back on every ordinary load.
 */
export function pruneStaleColumnRefs(
  settings: TableViewSettings,
  ignored: readonly IgnoredColumnRef[],
): TableViewSettings {
  if (ignored.length === 0) return settings;
  const gone = new Set(ignored.map((entry) => entry.columnId));
  const keep = (ids: readonly ColumnId[]) => ids.filter((id) => !gone.has(String(id)));

  const pruneGroup = (group: FilterGroup): FilterGroup | null => {
    const rules = group.rules.filter((rule) => !gone.has(String(rule.columnId)));
    const groups = group.groups
      .map((child) => pruneGroup(child))
      .filter((child): child is FilterGroup => child !== null);
    if (rules.length === 0 && groups.length === 0) return null;
    return { operator: group.operator, rules, groups };
  };

  const next: TableViewSettings = {
    ...settings,
    hiddenColumnIds: keep(settings.hiddenColumnIds),
    pinnedColumnIds: keep(settings.pinnedColumnIds),
    wrappedColumnIds: keep(settings.wrappedColumnIds),
    sort: settings.sort.filter((rule) => !gone.has(String(rule.columnId))),
    filter: settings.filter === null ? null : pruneGroup(settings.filter),
    groupBy:
      settings.groupBy !== null && gone.has(String(settings.groupBy.columnId)) ? null : settings.groupBy,
    ...(settings.columnOrder === undefined ? {} : { columnOrder: keep(settings.columnOrder) }),
    ...(settings.columnWidths === undefined
      ? {}
      : {
          columnWidths: Object.fromEntries(
            Object.entries(settings.columnWidths).filter(([id]) => !gone.has(id)),
          ),
        }),
  };
  return JSON.stringify(next) === JSON.stringify(settings) ? settings : next;
}

/* ── column order ─────────────────────────────────────────────────────────── */

export interface OrderedColumn {
  readonly id: ColumnId;
  readonly position: number;
}

/**
 * §4: `columnOrder` overrides `columns.position` per view; ids the board no longer has are
 * dropped and columns the order does not mention keep board order, appended after it (E1).
 */
export function applyColumnOrder<T extends OrderedColumn>(
  columns: readonly T[],
  order: readonly ColumnId[] | undefined,
): T[] {
  const byPosition = [...columns].sort((a, b) => a.position - b.position);
  if (!order || order.length === 0) return byPosition;
  const known = new Map(byPosition.map((column) => [column.id, column]));
  const out: T[] = [];
  for (const id of order) {
    const column = known.get(id);
    if (column) {
      out.push(column);
      known.delete(id);
    }
  }
  for (const column of byPosition) if (known.has(column.id)) out.push(column);
  return out;
}

/**
 * The visible order the grid renders: pinned prefix first (Q167), then the rest, hidden
 * columns removed. `name` can never be hidden `[B5§9 L10]` and never leaves position 0 (H4).
 */
export function visibleColumns<T extends OrderedColumn>(
  columns: readonly T[],
  settings: TableViewSettings,
  nameColumnId: ColumnId,
): { pinned: T[]; scrolling: T[] } {
  const hidden = new Set(settings.hiddenColumnIds.filter((id) => id !== nameColumnId));
  const ordered = applyColumnOrder(columns, settings.columnOrder).filter((column) => !hidden.has(column.id));
  const pinnedIds = new Set(settings.pinnedColumnIds);
  pinnedIds.add(nameColumnId);
  const pinned = ordered.filter((column) => pinnedIds.has(column.id));
  const scrolling = ordered.filter((column) => !pinnedIds.has(column.id));
  // H4: the name column is always first in the sticky region.
  pinned.sort((a, b) => (a.id === nameColumnId ? -1 : b.id === nameColumnId ? 1 : 0));
  return { pinned, scrolling };
}

/* ── filter tree helpers ──────────────────────────────────────────────────── */

export function countRules(group: FilterGroup | null): number {
  if (!group) return 0;
  return group.rules.length + group.groups.reduce((sum, child) => sum + countRules(child), 0);
}

export function isEmptyFilter(group: FilterGroup | null): boolean {
  return countRules(group) === 0;
}

/** E1: a rule referencing a deleted column is dropped on read, and the pane says how many. */
export function pruneFilter(
  group: FilterGroup | null,
  knownColumnIds: ReadonlySet<string>,
): { filter: FilterGroup | null; removed: number } {
  if (!group) return { filter: null, removed: 0 };
  let removed = 0;
  const rules = group.rules.filter((rule) => {
    const keep = knownColumnIds.has(rule.columnId);
    if (!keep) removed += 1;
    return keep;
  });
  const groups: FilterGroup[] = [];
  for (const child of group.groups) {
    const pruned = pruneFilter(child, knownColumnIds);
    removed += pruned.removed;
    // An **empty** group is kept: the advanced builder creates one before its first rule.
    groups.push(pruned.filter ?? { operator: child.operator, rules: [], groups: [] });
  }
  const filter: FilterGroup = { operator: group.operator, rules, groups };
  const nothingLeft = rules.length === 0 && groups.length === 0;
  return { filter: nothingLeft ? null : filter, removed };
}

/** Drops a deleted column out of every sort rule (E1 for `sort`). */
export function pruneSort(sort: readonly SortRule[], knownColumnIds: ReadonlySet<string>): SortRule[] {
  return sort.filter((rule) => knownColumnIds.has(rule.columnId));
}

/**
 * §8.5: the quick pane's selection is one OR-group per column, AND-ed together — and that is
 * exactly what "Switch to advanced filters" has to convert losslessly.
 */
export function quickFilterToGroup(selection: ReadonlyMap<ColumnId, readonly unknown[]>): FilterGroup | null {
  const groups: FilterGroup[] = [];
  for (const [columnId, values] of selection) {
    if (values.length === 0) continue;
    groups.push({
      operator: 'or',
      rules: values.map((value) => ({
        columnId,
        operator: value === BLANK_FACET ? ('is_empty' as FilterOperator) : ('any_of' as FilterOperator),
        compareValue: value === BLANK_FACET ? null : [value],
      })),
      groups: [],
    });
  }
  if (groups.length === 0) return null;
  return { operator: 'and', rules: [], groups };
}

/** The reverse: a filter the quick pane can display, or `null` when it is too complex. */
export function groupToQuickFilter(filter: FilterGroup | null): Map<ColumnId, unknown[]> | null {
  if (!filter) return new Map();
  if (filter.rules.length > 0 || filter.operator !== 'and') return null;
  const out = new Map<ColumnId, unknown[]>();
  for (const child of filter.groups) {
    if (child.groups.length > 0 || child.rules.length === 0) return null;
    const columnId = child.rules[0]!.columnId;
    if (child.rules.some((rule) => rule.columnId !== columnId)) return null;
    const values: unknown[] = [];
    for (const rule of child.rules) {
      if (rule.operator === 'is_empty') values.push(BLANK_FACET);
      else if (rule.operator === 'any_of' && Array.isArray(rule.compareValue)) {
        values.push(...(rule.compareValue as unknown[]));
      } else return null;
    }
    out.set(columnId, values);
  }
  return out;
}

/**
 * monday's own sentinel for "empty" inside a filter payload; the service translates it to
 * `is_empty` before it reaches SQL `[B4§5.3]`, `[B5§5.3]`. Kept verbatim so a filter we build
 * is a filter `/v2` accepts.
 */
export const BLANK_FACET = '$$$blank$$$';
